import { NavLink, Outlet, useNavigate } from "react-router-dom";
import { useEffect, useState } from "react";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import {
  LayoutDashboard, Radio, ShoppingBag, Sparkles, FileBarChart2,
  Bell, Users, Settings, LogOut, Command, ChevronRight, Swords
} from "lucide-react";

const NAV = [
  { to: "/", icon: LayoutDashboard, label: "Dashboard", key: "dashboard" },
  { to: "/channels", icon: Radio, label: "Channels", key: "channels" },
  { to: "/marketplace", icon: ShoppingBag, label: "Marketplace", key: "marketplace" },
  { to: "/competitors", icon: Swords, label: "Kompetitor", key: "competitors" },
  { to: "/ai", icon: Sparkles, label: "AI Assistant", key: "ai" },
  { to: "/reports", icon: FileBarChart2, label: "Laporan", key: "reports" },
  { to: "/notifications", icon: Bell, label: "Notifikasi", key: "notifications" },
];

const ROLE_LABEL = {
  super_admin: "SUPER ADMIN",
  marketing_manager: "MARKETING MGR",
  staff: "DM STAFF",
  viewer: "VIEWER",
};

export default function Layout() {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const [unread, setUnread] = useState(0);

  useEffect(() => {
    const load = () => api.get("/notifications", { params: { unread_only: true, limit: 1 } })
      .then(r => setUnread(r.data.unread_count || 0))
      .catch(() => {});
    load();
    const t = setInterval(load, 30000);
    return () => clearInterval(t);
  }, []);

  const link = ({ isActive }) =>
    `group flex items-center gap-3 px-4 py-3 border-l-2 transition-colors ${
      isActive
        ? "border-[#007AFF] bg-[#0A0A0A] text-white"
        : "border-transparent text-zinc-500 hover:text-white hover:bg-[#0A0A0A]"
    }`;

  return (
    <div className="min-h-screen flex bg-[#050505] text-white">
      {/* Sidebar */}
      <aside className="w-60 shrink-0 border-r border-[#1F1F1F] bg-[#080808] flex flex-col" data-testid="app-sidebar">
        <div className="px-4 py-5 border-b border-[#1F1F1F] flex items-center gap-2">
          <div className="w-8 h-8 bg-[#007AFF] flex items-center justify-center">
            <Command size={16} className="text-white" strokeWidth={2.5} />
          </div>
          <div className="leading-tight">
            <div className="font-display font-black text-sm tracking-tight">DMCC</div>
            <div className="label-mono text-[9px]">command center</div>
          </div>
        </div>

        <nav className="flex-1 py-3">
          {NAV.map((n) => (
            <NavLink key={n.key} to={n.to} end={n.to === "/"} className={link} data-testid={`nav-${n.key}`}>
              <n.icon size={16} strokeWidth={1.8} />
              <span className="text-sm">{n.label}</span>
              {n.key === "notifications" && unread > 0 && (
                <span data-testid="nav-notifications-badge"
                  className="ml-auto label-mono bg-[#FF3B30] text-white px-1.5 py-0.5 text-[9px] leading-none">
                  {unread > 99 ? "99+" : unread}
                </span>
              )}
              {!(n.key === "notifications" && unread > 0) && (
                <ChevronRight size={12} className="ml-auto opacity-0 group-hover:opacity-40 transition" />
              )}
            </NavLink>
          ))}
          {user?.role === "super_admin" && (
            <>
              <div className="mt-6 mb-2 px-4 label-mono">admin</div>
              <NavLink to="/users" className={link} data-testid="nav-users">
                <Users size={16} strokeWidth={1.8} /><span className="text-sm">Pengguna</span>
              </NavLink>
              <NavLink to="/settings" className={link} data-testid="nav-settings">
                <Settings size={16} strokeWidth={1.8} /><span className="text-sm">Pengaturan</span>
              </NavLink>
            </>
          )}
          {user?.role !== "super_admin" && (
            <NavLink to="/settings" className={link} data-testid="nav-settings-user">
              <Settings size={16} strokeWidth={1.8} /><span className="text-sm">Pengaturan</span>
            </NavLink>
          )}
        </nav>

        <div className="p-3 border-t border-[#1F1F1F]">
          <div className="flex items-center gap-3 px-2 py-2">
            <div className="w-8 h-8 bg-gradient-to-br from-[#007AFF] to-[#00F0FF] flex items-center justify-center font-mono text-xs font-bold">
              {user?.name?.[0]?.toUpperCase() || "U"}
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-sm truncate" data-testid="sidebar-user-name">{user?.name}</div>
              <div className="label-mono text-[9px] text-[#00E676]" data-testid="sidebar-user-role">
                {ROLE_LABEL[user?.role] || "USER"}
              </div>
            </div>
            <button
              onClick={logout}
              className="text-zinc-500 hover:text-[#FF3B30] transition p-1"
              data-testid="btn-logout"
              title="Keluar"
            >
              <LogOut size={16} />
            </button>
          </div>
        </div>
      </aside>

      {/* Main content */}
      <main className="flex-1 min-w-0 overflow-x-hidden bg-grid">
        <Outlet />
      </main>
    </div>
  );
}
